import { provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ApmModule } from '@elastic/apm-rum-angular';
import {
    AppBasicsModuleConfigModel,
    AppOrigin,
    AuthState,
    MfAuthGuard,
    provideAppBasics,
    provideMdsModal,
    provideMfCommonAuth,
    providePasswordCaptchaClient,
    USER_INITIALIZATION_SERVICE,
} from '@meddev/fe-shared';
import { MfCaptchaService, MfCaptchaStrategyEnum, provideMfCaptcha } from '@meddev/fe-shared/mf-captcha';
import { MF_NGB_DATEPICKER_CONFIG, type MfNgbDatepickerConfig } from '@meddev/ng-bootstrap';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { providedNgxDatatableConfig } from '@swimlane/ngx-datatable';
import { NgxMaskDirective, provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask';
import { MatomoInitializationMode, MatomoModule } from 'ngx-matomo-client';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { ScriptLoaderService } from './_services/script-loader.service';
import { DATE_CONST, DEFAULT_DATEPICKER_RANGES } from './_shared/constants/date-constants.const';
import { EnvironmentHelper } from './_shared/helper/environment-helper';
import { MedifitErrorDefinition } from './_shared/helper/medifit-error-definitions';
import { DatePipe } from './_shared/pipes/date.pipe';
import { RulesService } from './_shared/services';
import { KeyboardShortcutsGlobalService } from './_shared/services/keyboard-shortcuts-global.service';
import { KeyboardShortcutsTinyMCEService } from './_shared/services/keyboard-shortcuts-tinymce.service';
import { MenuService } from './_shared/services/menu.service';
import { BaseState } from './_shared/state/base/base.state';
import { CustomerState } from './_shared/state/customer/customer.state';
import { DeviceState } from './_shared/state/devices/device-state';
import { ExaminationGroupsState } from './_shared/state/examination-groups/examination-groups-state';
import { ExaminationState } from './_shared/state/examinations/examinations-state';
import { KanbanState } from './_shared/state/kanban/kanban.state';
import { LaboratoryState } from './_shared/state/laboratory/laboratory-state';
import { TermState } from './_shared/state/term/term.state';
import { WebsocketState } from './_shared/state/websocket/websocket.state';
import { routes } from './app.routes';
import { EambulantaUserInitService } from './auth/services/user-initialization.service';
import { PublicState } from './public/state/public.state';
import { LayoutModule } from './theme/layouts/layout.module';
import { AvailabilityState } from './theme/pages/settings/availability-calendar/_state/availability.state';
import { TelemedicineModule } from './theme/pages/telemedicine/telemedicine.module';

EnvironmentHelper.initEnvironment();

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            ApmModule,
            NgbModule,
            LayoutModule,
            BrowserModule,
            ToastrModule.forRoot({
                positionClass: 'toast-top-center',
            }),
            SweetAlert2Module.forRoot(),
            TelemedicineModule,
            MatomoModule.forRoot({
                mode: MatomoInitializationMode.AUTO_DEFERRED,
            }),
            NgxMaskDirective,
            NgxPermissionsModule.forRoot(),
        ),
        MfAuthGuard,
        provideRouter(routes),
        {
            provide: USER_INITIALIZATION_SERVICE,
            useClass: EambulantaUserInitService,
        },
        provideAppBasics({
            version: environment.VERSION,
            production: environment.production,
            releaseStage: environment.RELEASE_STAGE as AppBasicsModuleConfigModel['releaseStage'],
            matomo: {
                siteId: '16',
                trackerUrl: 'https://data.medifit.si/',
            },
            bugSnag: {
                apiKey: '4c8b0c632c20c7d5d987858bb90936a3',
            },
            apmServiceName: 'eambulanta-app',
            errorDefinitions: {
                errors: MedifitErrorDefinition.errors,
            } as AppBasicsModuleConfigModel['errorDefinitions'],
        }),
        provideMdsModal({
            default: {
                confirmButtonText: $localize`Potrdi`,
                cancelButtonText: $localize`Zapri`,
                denyButtonText: $localize`Prekliči`,
            },
            error: {
                title: $localize`Napaka`,
            },
        }),
        provideMfCommonAuth({
            authV1Url: environment.auth.v1.protocol + environment.auth.v1.rootUrl + environment.auth.v1.port + environment.auth.v1.url,
            authV2Url: environment.auth.v2.protocol + environment.auth.v2.rootUrl + environment.auth.v2.port + environment.auth.v2.url,
            authV2TokenPatterns: environment.mfCommonAuth.authV2TokenPatterns.map((p: string) => new RegExp(p)),
            loginRedirectUrl: environment.mfCommonAuth.loginRedirectUrl,
            realmName: environment.mfCommonAuth.realmName,
            clientId: environment.mfCommonAuth.clientId,
            impersonationTokenUrlParamName: environment.mfCommonAuth.impersonationTokenUrlParamName,
            additionalIgnoredPatterns: [new RegExp('content\\.medifit\\.si'), new RegExp('localhost:1337'), new RegExp('127.0.0.1:8080')],
            appOrigin: AppOrigin.EAMBULANTA,
        }),
        provideMfCaptcha({
            language: 'sl',
            v2: environment.reCaptcha.v2,
            v3: environment.reCaptcha.v3,
            defaultStrategy: MfCaptchaStrategyEnum.FALLBACK,
        }),
        providePasswordCaptchaClient(MfCaptchaService),
        providedNgxDatatableConfig({
            messages: {
                selectedMessage: '',
            },
        }),
        ScriptLoaderService,
        RulesService,
        { provide: LOCALE_ID, useValue: 'sl' },
        {
            provide: MF_NGB_DATEPICKER_CONFIG,
            useFactory: (): MfNgbDatepickerConfig => ({
                dateFormat: DATE_CONST.format.date.view,
                defaultRanges: DEFAULT_DATEPICKER_RANGES,
            }),
        },
        { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
        NgxPermissionsModule,
        MenuService,
        provideEnvironmentNgxMask(),
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({})), // TODO do we need this, and setup withXsrfConfiguration?
        provideAnimations(),
        provideStore(
            [
                AuthState,
                BaseState,
                WebsocketState,
                PublicState,
                AvailabilityState,
                ExaminationState,
                LaboratoryState,
                DeviceState,
                KanbanState,
                ExaminationGroupsState,
                TermState,
                CustomerState,
            ],
            { developmentMode: !environment.production },
            withNgxsStoragePlugin({
                keys: '*',
            }),
            withNgxsRouterPlugin(),
            withNgxsFormPlugin(),
            withNgxsReduxDevtoolsPlugin({
                disabled: environment.production,
            }),
            withNgxsLoggerPlugin({
                disabled: environment.production,
            }),
        ),
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
        provideAppInitializer(() => {
            inject(KeyboardShortcutsGlobalService)?.init();
            inject(KeyboardShortcutsTinyMCEService)?.init();
        }),
        DatePipe,
    ],
};
